import { videoLabel, type VideoMetricRow } from "@meta-video/contracts";
import { Modal, Stack } from "@mantine/core";
import { useMemo, type RefObject } from "react";

import { restoreFocus } from "../../app/a11y.js";
import type { VideoApi } from "./api.js";
import { StatusBadge } from "./StatusBadge.js";
import { VideoMetricSummary } from "./VideoMetricSummary.js";
import { VideoPlayer } from "./VideoPlayer.js";

export interface VideoPlaybackModalProps {
  readonly api?: VideoApi;
  readonly fallbackFocusRef?: RefObject<HTMLElement | null>;
  readonly onAccessChanged?: () => void;
  readonly onClose: () => void;
  readonly onSessionExpired?: () => void;
  readonly opened: boolean;
  readonly returnFocusRef?: RefObject<HTMLElement | null>;
  readonly row: VideoMetricRow;
  readonly videoMetricCoverageThreshold: number;
}

export function VideoPlaybackModal({
  api,
  fallbackFocusRef,
  onAccessChanged,
  onClose,
  onSessionExpired,
  opened,
  returnFocusRef,
  row,
  videoMetricCoverageThreshold,
}: VideoPlaybackModalProps) {
  const label = videoLabel(row);
  const selection = useMemo(() => ({
    brandId: row.brandId,
    videoId: row.videoId,
  }), [row.brandId, row.videoId]);
  return (
    <Modal
      centered
      closeButtonProps={{ "aria-label": "Close" }}
      onClose={onClose}
      onExitTransitionEnd={() => restoreFocus(
        returnFocusRef?.current ?? null,
        fallbackFocusRef?.current ?? null,
      )}
      opened={opened}
      returnFocus={false}
      size="min(72rem, 94vw)"
      title={label.primary}
    >
      <Stack className="video-playback-modal__content" gap="md">
        {row.playStatus === "hosted" ? (
          <VideoPlayer
            api={api}
            autoPrepare
            key={`${row.brandId}\0${row.videoId}`}
            label={label.primary}
            onAccessChanged={onAccessChanged}
            onSessionExpired={onSessionExpired}
            selection={selection}
            surface="report"
          />
        ) : <StatusBadge status={row.playStatus} />}
        <VideoMetricSummary
          row={row}
          videoMetricCoverageThreshold={videoMetricCoverageThreshold}
        />
      </Stack>
    </Modal>
  );
}
